import {
  useCallback, useEffect, useState,
} from 'react';
import {
  ApiResumeDetail, fetcher, isApiV1Errors, isResumeDetailStatusOk,
} from '@utils/data-fetching';
import { CvDetailObject } from '@components/templates/cv/interface/CvDetailInterface';
import { useTranslation } from 'next-i18next';
import { InterfaceJobFolderOptions } from '@components/templates/cv/sections/ProfileHeading';
import { useSwrResumeGetOperationJob } from '@hooks/useSwrApi/endpoints/resume-operations';
import { useSwrUserUnlockView } from '@hooks/useSwrApi/endpoints/resume-operations/useSwrUserUnlockView';
import { buildResumeFetchApiLink } from '@components/templates/cv/utils/buildResumeLink';
import { handleResumeErrorCode } from '@components/templates/cv/utils/handleResumeErrorCode';

interface IProps {
  slug?: string;
  jobSlug?: string;
  isPassiveRecommendation?: boolean;
  rid?: string;
  isCompany?: boolean;
  toFetchOperationJob?: boolean;
  locale: Locale;
  waitForSlug?: boolean;
}

/**
 *
 * @param slug - the slug of the cv
 * @param isCompany
 * @param toFetchOperationJob - if we want to fetch the operation job folder
 * @param rid - query param. can be used in both cases. with slug or jobSlug
 * @param jobSlug - used in job context. used with slug
 * @param isPassiveRecommendation - used for letting us know if the cv is a passive recommendation
 * @param locale - the current app locale.
 * @param waitForSlug - if we should wait for the slug to be available before fetching the data.
 */
export const useCvDetails = ({
  slug, isCompany, toFetchOperationJob, rid, jobSlug, locale, waitForSlug = false, isPassiveRecommendation,
}: IProps) => {
  const { t } = useTranslation('common');
  const [cvDetail, setCvDetail] = useState<CvDetailObject>();
  const [isCvLoaded, setCvLoaded] = useState<boolean>(false);
  const [showBlurredCv, setShowBlurredCv] = useState<boolean>(false);
  const [cvCurrentJobUnlockFolder, setCvCurrentJobUnlockFolder] = useState<InterfaceJobFolderOptions>();
  const [phoneHighlight, setPhoneHighlight] = useState<boolean>(false);
  const [availableResumeInformations, setAvailableResumeInformations] = useState<CvDetailObject>();
  const [fetchOperationJobId, setFetchOperationJobId] = useState<number | undefined>(undefined);
  const [fetchOperationJobSlug, setFetchOperationJobSlug] = useState<string | undefined>(undefined);

  // By default, every cv detail has a folder job id, if we do operations on some folders like shortlist/interview
  // on some job folder from select we need to fetch data in that job context
  const [jobFolderInfo, setJobFolderInfo] = useState<InterfaceJobFolderOptions>({
    jobId: 0,
    jobSlug: '',
    title: '',
  });

  // SWR Mutation - Get Operation Job
  // ************************************
  const {
    data: operationJob,
    trigger: triggerFetchOperationJob,
    isMutating: isLoadingOperationJob,
  } = useSwrResumeGetOperationJob({
    jobId: fetchOperationJobId,
    jobSlug: fetchOperationJobSlug,
    locale,
    successCallback: (response) => {
      setCvCurrentJobUnlockFolder(response);
      setJobFolderInfo(response);
    },
  });

  const handleCvResponse = useCallback(
    (response: CvDetailObject) => {
      if (response?.blurred) {
        setShowBlurredCv(true);
        setAvailableResumeInformations(response);
        setCvLoaded(true);
        return;
      }

      setShowBlurredCv(false);
      setCvDetail(response);

      if (isCompany) {
        if (response?.application?.jobId) {
          setFetchOperationJobId(response.application.jobId);
          setFetchOperationJobSlug(undefined);
        } else if (jobSlug) {
          // In job context with no application yet (e.g. recommended user): fetch job info by slug
          setFetchOperationJobId(undefined);
          setFetchOperationJobSlug(jobSlug);
        } else if (response?.jobMatches?.length) {
          const matchedJob = response.jobMatches.find((match) => match.jobId);
          setFetchOperationJobId(matchedJob?.jobId ?? 0);
          setFetchOperationJobSlug(undefined);
        } else {
          setFetchOperationJobId(0);
          setFetchOperationJobSlug(undefined);
        }
      }

      setCvLoaded(true);
    },
    [isCompany, jobSlug],
  );


  const {
    trigger: triggerPayPerView,
  } = useSwrUserUnlockView({
    slug: slug ?? '',
    locale,
    successCallback: (response) => {
      handleCvResponse(response as CvDetailObject);
    },
    errorCallback: () => {
    },
  });


  const applicantSeenContactDefault = typeof cvDetail?.application?.applicantSeenContact === 'boolean'
    ? cvDetail?.application?.applicantSeenContact : false;
  const [applicantSeenContactStatus, setApplicantSeenContactStatus] = useState<boolean>(applicantSeenContactDefault);

  const toastErrorMessage = t('global.status.401-unauthorized.message');


  // Hook to fetch operation job
  // ************************************
  useEffect(() => {
    if (isCompany && (toFetchOperationJob || fetchOperationJobSlug)) {
      void triggerFetchOperationJob();
    }
  }, [toFetchOperationJob, fetchOperationJobId, fetchOperationJobSlug, triggerFetchOperationJob, isCompany]);


  const onPayPerViewCallback = useCallback(() => {
    void triggerPayPerView();
  }, [triggerPayPerView]);

  // fetch resume detail
  const fetchResumeDetail = useCallback((onSuccessCallback?: (cv: CvDetailObject) => void) => {
    // create url based on the context
    const resumeFetchApiLink = buildResumeFetchApiLink(slug, rid, jobSlug, isPassiveRecommendation);
    // fetch the data
    fetcher<ApiResumeDetail>(resumeFetchApiLink, locale)
      .then((response) => {
        if (isResumeDetailStatusOk(response)) {
          setPhoneHighlight(false);
          handleCvResponse(response);
          if (onSuccessCallback) {
            onSuccessCallback(response);
          }
        } else if (isApiV1Errors(response)) {
          setCvLoaded(false);
          handleResumeErrorCode({
            response,
            locale,
            slug: slug ?? '',
            jobSlug,
            toastErrorMessage,
            onPayPerViewCallback,
            onSearchPackActivateCallback: () => {
              fetchResumeDetail();
            },
          });
        }
      }).catch(() => {
      });
  }, [locale, slug, jobSlug, rid, toastErrorMessage, isPassiveRecommendation, onPayPerViewCallback, handleCvResponse]);

  // Populate the CV Detail
  useEffect(() => {
    if (waitForSlug && slug) fetchResumeDetail();
    if (!waitForSlug) fetchResumeDetail();
  }, [fetchResumeDetail, waitForSlug, slug]);

  useEffect(() => {
    const applicantSeenContactRestore = typeof cvDetail?.application?.applicantSeenContact === 'boolean'
      ? cvDetail?.application?.applicantSeenContact : false;
    setApplicantSeenContactStatus(applicantSeenContactRestore);
  }, [cvDetail]);

  return {
    cvDetail,
    fetchResumeDetail,
    showBlurredCv,
    isCvLoaded,
    setCvLoaded,
    availableResumeInformations,
    cvCurrentJobUnlockFolder,
    phoneHighlight,
    setPhoneHighlight,
    applicantSeenContactStatus,
    setApplicantSeenContactStatus,
    jobFolderInfo,
    setJobFolderInfo,
    fetchOperationJobId,
    setFetchOperationJobId,
    operationJob,
    isLoadingOperationJob,
  };
};
